import type { StripeSchoolLicence } from '@kimuno/api-sdk';
import { borderRadius, palette, spaces } from '@kimuno/theme';
import { Box, Chip, Skeleton, Typography } from '@mui/material';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncActionStatus } from '../../actions/common.actionTypes';
import {
	deriveDateCards,
	deriveIncludedProducts,
	deriveLicenceStatus,
	formatSubscriptionDate,
	STATUS_CHIPS,
} from '../../utils/schoolLicenceOverview';
import SettingItem from '../SettingItem';

interface Props {
	licence: StripeSchoolLicence | null | undefined;
	status: AsyncActionStatus | undefined;
}

const gridSx = {
	display: 'grid',
	gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
	gap: spaces.md,
} as const;

const dateCardsSx = { display: 'grid', gridTemplateColumns: '1fr', gap: spaces.md } as const;

const cardTitleSx = {
	fontFamily: 'Borna',
	fontSize: '18px',
	fontStyle: 'normal',
	fontWeight: 500,
	lineHeight: '28px',
	letterSpacing: '0.15px',
} as const;

// Read-only on purpose: a teacher can neither see the school's invoices nor
// manage its subscription, so no state of this card offers an action.
const SchoolLicenceCard: React.FC<Props> = ({ licence, status }) => {
	const { t } = useTranslation();

	if (status === undefined || status === AsyncActionStatus.LOADING) {
		return (
			<Box data-testid="school-licence-skeleton" sx={{ ...gridSx, paddingY: spaces.md }}>
				<Skeleton variant="rounded" height={180} />
				<Box sx={dateCardsSx}>
					<Skeleton variant="rounded" height={82} />
					<Skeleton variant="rounded" height={82} />
				</Box>
			</Box>
		);
	}

	// A failed read leaves `licence` unset: the title alone is still true.
	const licenceStatus = licence ? deriveLicenceStatus(licence) : undefined;

	return (
		<Box>
			<Typography variant="h6" color={palette.text.primary} sx={{ paddingY: spaces.md }}>
				{t('Subscription Information')}
			</Typography>
			<Box sx={gridSx}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						borderRadius: borderRadius.lg,
						border: `1px solid ${palette.elevation.outlined}`,
						padding: spaces.md,
						backgroundColor: palette.background.paperElevation2,
						gap: spaces.xs,
					}}
				>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<Typography color="text.primary" sx={cardTitleSx}>
							{t('meinUnterricht school licence')}
						</Typography>
						{licenceStatus && (
							<Chip
								label={t(STATUS_CHIPS[licenceStatus].label)}
								color={STATUS_CHIPS[licenceStatus].color}
							/>
						)}
					</Box>
					{licence && (
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spaces.xs }}>
							{deriveIncludedProducts(licence).map((product) => (
								<Chip key={product} label={t(product)} />
							))}
						</Box>
					)}
				</Box>

				{licence && licenceStatus && (
					<Box sx={dateCardsSx}>
						{deriveDateCards(licence, licenceStatus).map((card) => (
							<SettingItem key={card.title} title={t(card.title)}>
								<Typography>{formatSubscriptionDate(card.date)}</Typography>
							</SettingItem>
						))}
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default SchoolLicenceCard;
